/**
 * RedemptionHistorySection Component
 * Displays user's redemption history with pagination
 * Uses infinite query with "Load More" button
 */

import { useRedemptionHistory } from '../../hooks/useRedeemApi';
import { LoadingSpinner } from '../ui';
import { FormButton } from '../form/FormButton';

export function RedemptionHistorySection() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useRedemptionHistory(20);

  // Flatten all pages into a single array
  const allRedemptions = data?.pages.flatMap(page => page.redemptions) || [];

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Redemption History
        </h2>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Redemption History
        </h2>
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">
            Failed to load redemption history
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Redemption History
      </h2>

      {allRedemptions.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            No redemptions yet
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Redeemed codes will appear here
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Code
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Group
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Items Granted
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Redeemed
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {allRedemptions.map(redemption => {
                  const itemsGranted = [
                    ...redemption.entitlementIds.map(
                      id => `Entitlement: ${id}`
                    ),
                    ...redemption.badgeIds.map(id => `Badge: ${id}`),
                  ];

                  return (
                    <tr key={redemption.code}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-mono text-gray-900 dark:text-white">
                          {redemption.code}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {redemption.groupName}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {itemsGranted.length === 0 ? (
                            <span className="text-gray-500 dark:text-gray-400">
                              No items
                            </span>
                          ) : (
                            <ul className="list-disc list-inside space-y-1">
                              {itemsGranted.map((item, idx) => (
                                <li key={idx} className="text-xs">
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(redemption.redeemedAt).toLocaleDateString(
                            undefined,
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            }
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Load More Button */}
          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <FormButton
                type="button"
                onClick={() => fetchNextPage()}
                isLoading={isFetchingNextPage}
                loadingText="Loading..."
                className="btn-secondary"
              >
                Load More
              </FormButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}
